import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  Box,
  Badge,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import { getStyles } from "src/styles/theme";
import defaultStyles from "./styles";
import { useAppSelector } from "src/redux/store";
import PreviewIcon from "@mui/icons-material/Preview";

export type DashBoardTable = {
  topHeader: string[];
  data: any;
  onEdit?: (row: any) => void;
  onDelete?: (row: any) => void;
  onQueryClick?: (row: any) => void;
  status?: string;
};

export function UpdateProgress({
  data,
  topHeader,
  onEdit,
  onQueryClick,
  onDelete,
  status,
}: DashBoardTable) {
  const styles = getStyles(defaultStyles);

  const user = useAppSelector((state) => state.root.user);

  const calculateRowSpan = (data, key) => {
    const rowSpans = {};
    let count = 0;
    let currentKey = null;

    data.forEach((row, index) => {
      if (row[key] !== currentKey) {
        currentKey = row[key];
        count = 1;
        rowSpans[index] = 1;
      } else {
        count += 1;
        rowSpans[index - count + 1] = count;
      }
    });

    return rowSpans;
  };

  const formatDecimal = (value: any) => {
    if (value === null || value === undefined || value === "") return value;
    const num = Number(value);
    return Number.isNaN(num) ? value : num.toFixed(2);
  };

  const financialProgress = data.reduce(
    (sum, item) => sum + (Number(item.costProgress) || 0),
    0
  );
  const totalManDays = data.reduce(
    (sum, item) => sum + (Number(item.data?.totalManDays) || 0),
    0
  );
  const isTotalRow = (index: number) => index === data.length - 1;
  const updatedData = data.map((item, index) => ({
    ...item,
    costRequired: formatDecimal(item.costRequired),
    costProgress: isTotalRow(index)
      ? financialProgress.toFixed(2)
      : formatDecimal(item.costProgress),
    manDays: isTotalRow(index)
      ? Number.isInteger(totalManDays)
        ? totalManDays
        : totalManDays.toFixed(2)
      : item.data?.totalManDays,
  }));

  // Calculate row spans for `mainComponent` and `year`
  const mainComponentRowSpan = calculateRowSpan(data, "mainComponent");
  const yearRowSpan = calculateRowSpan(data, "year");

  return (
    <TableContainer component={Paper}>
      <Table sx={{ minWidth: 600 }} aria-label="simple table">
        <TableHead {...styles("header")}>
          <TableRow>
            {topHeader.map((item, index) => (
              <TableCell align="center" {...styles("cell")} key={index}>
                {item}
              </TableCell>
            ))}
            {status === "SUBMIT_TO_SNO" &&
              (user?.data?.userType === null ? (
                <TableCell align="center" {...styles("cell")}>
                  Update Progress
                </TableCell>
              ) : (
                <TableCell align="center" {...styles("cell")}>
                  View Detailed Progress
                </TableCell>
              ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {updatedData.map((row, index) => {
            return (
              <TableRow key={index}>
                {yearRowSpan[index] && (
                  <TableCell
                    align="left"
                    rowSpan={yearRowSpan[index]}
                    {...styles("cell", { width: "100px" })}
                  >
                    {row.year}
                  </TableCell>
                )}
                {/* Main Component Column with Row Span */}
                {mainComponentRowSpan[index] && (
                  <TableCell
                    align="left"
                    rowSpan={mainComponentRowSpan[index]}
                    {...styles("cell", { width: "200px" })}
                  >
                    {row.mainComponent}
                  </TableCell>
                )}

                <TableCell align="left" {...styles("cell", { width: "250px" })}>
                  {row?.subComponent}
                </TableCell>
                <TableCell align="left" {...styles("cell", { width: "250px" })}>
                  {row.perticularOfItem}
                </TableCell>
                <TableCell align="left" {...styles("cell", { width: "100px" })}>
                  {row.unit}
                </TableCell>
                <TableCell align="left" {...styles("cell", { width: "100px" })}>
                  {row.numberOfUnits}
                </TableCell>
                <TableCell align="left" {...styles("cell", { width: "100px" })}>
                  {row.costRequired}
                </TableCell>
                <TableCell align="left" {...styles("cell", { width: "100px" })}>
                  {row.progressInUnits}
                </TableCell>
                <TableCell align="left" {...styles("cell", { width: "100px" })}>
                  {row.costProgress}
                </TableCell>
                <TableCell align="left" {...styles("cell", { width: "100px" })}>
                  {row.manDays}
                </TableCell>
                {row.status === "SUBMIT_TO_SNO" &&
                  index !== data.length - 1 && (
                    <TableCell
                      align="center"
                      {...styles("cell", { width: "100px" })}
                    >
                      <>
                        {user?.data?.userType === null ? (
                          <Typography
                            variant="body2"
                            color="primary"
                            onClick={() => onEdit(row)}
                            style={{
                              cursor: "pointer",
                              textDecoration: "underline",
                              fontSize: "16px",
                            }}
                          >
                            Update
                          </Typography>
                        ) : (
                          <Typography
                            variant="body2"
                            color="primary"
                            onClick={() => onEdit(row)}
                            style={{
                              cursor: "pointer",
                              textDecoration: "underline",
                              fontSize: "16px",
                              margin: "auto",
                              marginBottom: "10px",
                            }}
                          >
                            View
                          </Typography>
                        )}
                        <Box
                          onClick={() => onQueryClick && onQueryClick(row)}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            padding: "4px 8px",
                            borderRadius: "4px",
                            transition: "all 0.3s ease",
                            "&:hover": {
                              backgroundColor: "#e3f2fd",
                              transform: "scale(1.05)",
                            },
                          }}
                        >
                          <Badge
                            badgeContent={row?.data?.pendingQueryCount || 0}
                            color="error"
                            max={99}
                            sx={{
                              "& .MuiBadge-badge": {
                                fontSize: "10px",
                                height: "18px",
                                minWidth: "18px",
                                padding: "0 4px",
                              },
                            }}
                          >
                            <ChatBubbleOutlineIcon
                              fontSize="small"
                              sx={{ color: "#2196f3", margin: "auto" }}
                            />
                          </Badge>
                        </Box>
                      </>
                    </TableCell>
                  )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
